import * as React from 'react';

// Utils
import { t } from '@toolkit/util/i18n';
import {
  any as ramdaAny,
  flatten,
  formatCents,
  isNil,
  isNilOrEmpty,
  isNotNilOrEmpty,
  pipe,
  pluck,
  propEq,
} from '@src/shared/src/util/general';
import { notify } from '@toolkit/util/app';
// Constants
import { NOTIF_TYPE, SEAT_RES_STATUS, VEHICLE_TYPES } from '@src/shared/src/const/app';
// Actions
// Models & Interfaces
import {
  BookingTransportFareModel,
  SeatMapModel,
  SelectedSeatModel,
  TransportSegmentModel,
} from '@src/shared/src/models';
import { SeatAssignmentsInfo } from './SeatAssignmentsInfo';
// Components
// Styles

type Props = {
  fare: BookingTransportFareModel;
};

const getSelectedSeatMaps = (segment: TransportSegmentModel): SeatMapModel[] => {
  if (isNil(segment)) return [];

  const seatMaps = [segment, ...(segment.segments || [])].flatMap((s) => s.seatMaps || []);

  return seatMaps.filter((seatMap) => !isNilOrEmpty(seatMap.selectedSeats));
};

const getSelectedSeats = (fare: BookingTransportFareModel): SelectedSeatModel[] =>
  getSelectedSeatMaps(fare.segment).flatMap((seatMap) => seatMap.selectedSeats);

const formatSelectedSeatMap = (seatMap: SeatMapModel) => {
  const selectedSeats = seatMap.selectedSeats.map((selectedSeat) => selectedSeat.display).join(', ');
  if (seatMap.flightNumber) return `${seatMap.flightNumber}: ${selectedSeats}`;

  return selectedSeats;
};

const hasSeatReservation = (fare: BookingTransportFareModel) => {
  if (fare.segment.vehicles.includes(VEHICLE_TYPES.PLANE)) {
    return isNotNilOrEmpty(getSelectedSeats(fare));
  } else {
    return (
      fare.seatReservation === SEAT_RES_STATUS.AVAILABLE ||
      fare.seatReservation === SEAT_RES_STATUS.INCLUDED
    );
  }
};

const getSeatReservationPrice = (fare: BookingTransportFareModel) => {
  if (fare.segment.vehicles.includes(VEHICLE_TYPES.PLANE)) {
    return getSelectedSeats(fare).reduce((sum, selectedSeat) => sum + selectedSeat.price, 0);
  } else if (fare.seatReservation === SEAT_RES_STATUS.AVAILABLE) {
    return fare.seatReservationPrice;
  }
};

const hasDiscountedSeat = (fare: BookingTransportFareModel) =>
  ramdaAny(propEq('discounted', true))(getSelectedSeats(fare));

const getSeatReservationDetails = (fare: BookingTransportFareModel) => {
  const selectedSeatMaps = getSelectedSeatMaps(fare.segment);
  if (isNotNilOrEmpty(selectedSeatMaps)) {
    return (
      <span className="lp-booking-info-item-label">
        {t('BookingItem.label.seat')}&nbsp;
        {selectedSeatMaps.map(formatSelectedSeatMap).join('; ')}
      </span>
    );
  }
};

const hasSeatAssignmentsInfo = (fare: BookingTransportFareModel) =>
  pipe(pluck('seatAssignments'), flatten, isNotNilOrEmpty)(fare.segment.segments);

export const TransportationBookingSeatInfo: React.FC<Props> = ({ fare }) => {
  const seatReservationPrice = getSeatReservationPrice(fare);

  if (hasDiscountedSeat(fare)) {
    notify(t('transportationBookingInfo.seat.discounted.message'), NOTIF_TYPE.SUCCESS);
  }

  if (hasSeatReservation(fare)) {
    return (
      <div>
        <div className="lp-booking-info-item-sub_title">
          <i className="icon-seating" />
          {t('BookingItem.label.seatReservation')}:{' '}
          {!isNil(seatReservationPrice)
            ? ` +${formatCents(seatReservationPrice)}`
            : ` ${t('tariffFareTerms.label.seatReservation.included')}`}
        </div>
        {getSeatReservationDetails(fare)}

        {hasSeatAssignmentsInfo(fare) && (
          <SeatAssignmentsInfo
            segmentVehicles={fare.segment.vehicles}
            subSegments={fare.segment.segments}
          />
        )}
      </div>
    );
  }

  return null;
};
